/**
 * ADR-0077 (22 Oct, JWP sweep): one app-wide rule for reads that hit the server's time limit.
 *
 * Every GET read on the backend is bounded at the same 60 s as `spring.transaction.default-timeout`
 * (ReadStatementTimeoutFilter); past it the statement is stopped and the API answers 422
 * "The query has timed out." — or nginx answers 504 at its own 60 s. PowerBuilder runs a retrieve
 * once and reports; it never re-runs it on its own. The app-wide TanStack `retry: 1` used to turn
 * one 60 s timeout into a second 60 s attempt — two minutes of spinner before any message
 * (Item Search, slice-4394, fixed that for one screen; this is the same rule for every query).
 */

/** Operator text for a read that ran out of time. Contains "timed out" on purpose: screens that
 *  already pattern-match the server text (Item Search's searchErrorMessage) keep matching. */
export const READ_TIMEOUT_MESSAGE =
  'The query timed out after 60 seconds and was stopped. Narrow the criteria and try again.';

/** True for a server statement timeout (422 "The query has timed out.") or a proxy 504. */
export function isTimeoutError(err: unknown): boolean {
  if (err == null || typeof err !== 'object') return false;
  const status = (err as { status?: unknown }).status;
  if (status === 504) return true;
  const msg = err instanceof Error ? err.message : '';
  return /timed? ?out/i.test(msg);
}

/** TanStack `retry` for every query: retry a failure once, but never a timeout. */
export function retryUnlessTimeout(failureCount: number, error: unknown): boolean {
  if (isTimeoutError(error)) return false;
  return failureCount < 1;
}
